/**
 * @fileoverview Production Order Target table component
 * Displays output batches produced by production orders
 */
import React, { forwardRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import { DmsEntityTable } from "../DmsEntityTable";

/**
 * Format datetime for display
 * @param {string} dateStr - Date string
 * @returns {string} Formatted datetime
 */
const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Production Order Target table component using DmsEntityTable
 */
const ProductionOrderTargetTable = forwardRef(({ onViewDetails = null }, ref) => {
  // Column definitions
  const columns = useMemo(() => [
    {
      label: intl.get("ProductionOrder").d("Production Order"),
      column_name: "production_order",
      width: "180px",
      filter: true,
      element: (row) => row.production_order?.order_number || "-",
    },
    {
      label: intl.get("OutputBatch").d("Produced Batch"),
      column_name: "batch",
      width: "150px",
      filter: true,
      element: (row) => row.batch?.number || "-",
    },
    {
      label: intl.get("ProductDescription").d("Product Description"),
      column_name: "product_description",
      width: "200px",
      sortable: false,
      element: (row) => row.batch?.part?.description || "-",
    },
    {
      label: intl.get("Quantity").d("Quantity"),
      column_name: "quantity",
      width: "120px",
      function: (value) => value ? value.toLocaleString("pt-BR") : "0",
    },
    {
      label: intl.get("Expiration").d("Expiration"),
      column_name: "expiration",
      width: "120px",
      sortable: false,
      element: (row) => {
        if (!row.batch?.expiration) return "-";
        const date = new Date(row.batch.expiration);
        return date.toLocaleDateString("pt-BR");
      },
    },
    {
      label: intl.get("CreatedOn").d("Created On"),
      column_name: "created_on",
      width: "180px",
      function: formatDateTime,
    },
  ], []);

  // View details handler
  const handleViewDetails = useCallback((row) => {
    if (onViewDetails) {
      onViewDetails(row);
    }
  }, [onViewDetails]);

  return (
    <DmsEntityTable
      ref={ref}
      url="/production_order_target/table"
      columns={columns}
      onView={handleViewDetails}
      hideDelete={true}
      hideBlock={true}
      hideCreate={true}
      hidePdf={true}
      hideExport={true}
      createButtonLabel={intl.get("ProductionOrderTargetsReadOnly").d("Data synced from SAP (read-only)")}
    />
  );
});

ProductionOrderTargetTable.displayName = "ProductionOrderTargetTable";

ProductionOrderTargetTable.propTypes = {
  onViewDetails: PropTypes.func,
};


export { ProductionOrderTargetTable };


